import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { DatePicker as AntdDatePicker, Input, Tooltip } from 'antd';

import { useTranslation } from 'react-i18next';

import Table from '@components/Table';
import {
  bytesFormat,
  dateFormat,
  hourFormat,
  getDateFromBRPattern,
  getDateWithISOFormat,
} from '@utils/formatters';
import moment, { Moment } from 'moment';
import { VscRadioTower } from 'react-icons/vsc';
import useSwr from '@hooks/useSwr';
import RequestReport from '@components/RequestReport';
import KeycloakContext from '@providers/Keycloak/KeycloakContext';
import useSearchParams from '@hooks/useSearchParams';
import rolesConstants from '@utils/rolesConstants';
import StatusMenu from '@components/StatusMenu';
import RequestDailySessionReport from '@components/RequestDailySessionReport';
import SessionLocationArray, {
  ISessionLocationData,
} from './SessionLocationArray';
import AccountsModal from './ModalDetailsAccounts';

const { Search } = Input;

type TableType = 'accounts' | 'day' | 'month' | 'cycle';

interface IAuthentications {
  onlyMsisdn?: number | string | undefined;
  tableOnly?: boolean;
}

const Sessions = ({ onlyMsisdn, tableOnly = false }: IAuthentications) => {
  const {
    t,
    i18n: { language },
  } = useTranslation('sessions');

  const [locationArrayInfo, setLocationArrayInfo] = useState<
    ISessionLocationData[] | undefined
  >(undefined);

  const { keycloak } = useContext(KeycloakContext);
  const customerId = keycloak?.idTokenParsed?.customerId;
  const [msisdnValue, setMsisdnValue] = useState('');
  const [imsiValue, setImsiValue] = useState('');
  const [isLocationOpen, setIsLocationOpen] = useState(false);
  const [dataToShow, setDataToShow] = useState<TableType>(
    tableOnly ? 'accounts' : 'day',
  );

  const {
    setSearchParams,
    hasValuesInExtraParams,
    clearSearchParams,
    searchParams,
  } = useSearchParams({
    type: 'pagination',
    extraParams: [
      'msisdn',
      'imsi',
      'month',
      'year',
      'date',
      'initDate',
      'endDate',
    ],
  });

  const {
    year,
    month,
    msisdn,
    imsi,
    page,
    linesPerPage,
    date,
    initDate,
    endDate,
  } = useMemo(() => searchParams, [searchParams]);

  const handleShowLocationInfoArrayClick = useCallback(
    (value: ISessionLocationData[]) => {
      setLocationArrayInfo(value);
      setIsLocationOpen(true);
    },
    [],
  );

  const defaultParams = useMemo(
    () => ({
      msisdn: onlyMsisdn ?? msisdn,
      imsi,
      page: page ? Number(page) - 1 : 0,
      linesPerPage: linesPerPage ?? 20,
    }),
    [imsi, linesPerPage, msisdn, onlyMsisdn, page],
  );

  const byMonthParams = useMemo(
    () => ({
      ...defaultParams,
      year: year ?? moment().year(),
      month: month ?? moment().month() + 1,
    }),
    [defaultParams, month, year],
  );

  const byDayParams = useMemo(
    () => ({
      ...defaultParams,
      date: date
        ? getDateWithISOFormat(date).split('T')[0].split('-').join('-')
        : moment().subtract(1, 'days').format('YYYY-MM-DD'),
    }),
    [defaultParams, date],
  );

  const byCycleParams = useMemo(
    () => ({
      ...defaultParams,
      year: year ?? moment().year(),
      month: month ?? moment().month() + 1,
    }),
    [defaultParams, year, month],
  );

  const byAccountsParams = useMemo(
    () => ({
      ...defaultParams,
      initDate: initDate || moment(new Date()).format('YYYY-MM-DD'),
      endDate: endDate || moment(new Date()).format('YYYY-MM-DD'),
    }),
    [defaultParams, endDate, initDate],
  );

  const { data: dataByAccounts } = useSwr(
    dataToShow === 'accounts' ? 'service-proxy/accts/accounts' : null,
    {
      params: byAccountsParams,
    },
  );

  const { data: dataByDay } = useSwr(
    dataToShow === 'day' ? 'service-proxy/accts/by-day' : null,
    {
      params: byDayParams,
    },
  );

  const { data: dataByMonth } = useSwr(
    dataToShow === 'month' ? 'service-proxy/accts/by-month' : null,
    {
      params: byMonthParams,
    },
  );

  const { data: dataByCycle } = useSwr(
    dataToShow === 'cycle' ? 'service-proxy/accts/by-cycle' : null,
    {
      params: byCycleParams,
    },
  );

  const dataList = useMemo(() => {
    return {
      month: dataByMonth,
      day: dataByDay,
      cycle: dataByCycle,
      accounts: dataByAccounts,
    };
  }, [dataByMonth, dataByDay, dataByCycle, dataByAccounts]);

  // @ts-ignore
  const data = useMemo(() => dataList?.[dataToShow], [dataToShow, dataList]);

  useEffect(() => {
    setMsisdnValue(msisdn);
  }, [msisdn]);

  useEffect(() => {
    setImsiValue(imsi);
  }, [imsi]);

  const onChangeInitialDate = useCallback(
    (dateChange: any, dateString: any) => {
      const formattedDate = getDateFromBRPattern(dateString);
      setSearchParams({ date: formattedDate });
    },
    [setSearchParams],
  );

  const columns = useMemo(() => {
    if (dataToShow === 'accounts' && tableOnly) {
      return [
        {
          title: t('table.apn'),
          dataIndex: 'apn',
        },
        {
          title: t('table.ip'),
          width: `${t('table.ip').length + 6}ch`,
          dataIndex: 'ip',
        },
        {
          title: t('table.technology'),
          dataIndex: 'technology',
          width: `${t('table.technology').length + 6}ch`,
        },
        {
          title: t('table.operator'),
          dataIndex: 'operator',
        },
        {
          title: t('table.consume'),
          dataIndex: 'consumption',
          render: bytesFormat,
        },
        {
          title: t('table.duration'),
          dataIndex: 'sessionTime',
          width: `${t('table.duration').length + 6}ch`,
          render: hourFormat,
        },
        {
          title: t('table.acctStartTime'),
          dataIndex: 'startTime',
          width: `${t('table.acctStartTime').length + 6}ch`,
          render: (value: any) => dateFormat(value),
        },
        {
          title: t('table.acctStopTime'),
          dataIndex: 'stopTime',
          width: `${t('table.acctStopTime').length + 6}ch`,
          render: (value: any) => dateFormat(value),
        },
      ];
    }

    const allColumns: any[] = [
      {
        title: t('table.imsi'),
        dataIndex: 'imsi',
      },
      {
        title: t('table.msisdn'),
        dataIndex: 'msisdn',
      },
      {
        title: t('table.imei'),
        dataIndex: 'imei',
      },
      {
        title: t('table.apn'),
        dataIndex: 'apn',
      },
    ];
    if (dataToShow === 'month' || dataToShow === 'cycle') {
      allColumns.push(
        ...[
          {
            title: t('table.acctDate'),
            dataIndex: 'month',
            render: (value: string, record: any) => {
              const { year: yearNumber } = record;
              const monthName = t(`months.${value}`);
              if (language !== 'en') {
                return `${value ? monthName : ''}${
                  yearNumber ? `/${yearNumber}` : ''
                }`;
              }
              return `${yearNumber ?? ''}${value ? `/${monthName}` : ''}`;
            },
          },
          {
            title: t('table.consume'),
            dataIndex: 'totalOutputBytes',
            align: 'center' as const,
            render: (value: any, record: any) => {
              const sum = (value ?? 0) + (record?.totalInputBytes ?? 0);
              const result = bytesFormat(sum);
              return result;
            },
          },
          {
            title: t('table.duration'),
            dataIndex: 'totalSessionTime',
            align: 'center' as const,
            render: hourFormat,
          },
          {
            title: t('table.timeConnected'),
            dataIndex: 'uptime',
            align: 'center' as const,
            render: (value: any) => (value ? `${value?.toFixed(2)}%` : '0%'),
          },
        ],
      );
    }

    if (dataToShow === 'day') {
      allColumns.push(
        ...[
          {
            title: t('table.date'),
            dataIndex: 'date',
            align: 'center' as const,
            render: (value: string) =>
              value && moment.utc(value).add(3, 'hours').format('DD/MM/YYYY'),
          },
          {
            title: t('table.consume'),
            dataIndex: 'totalOutputBytes',
            align: 'center' as const,
            render: (value: any, record: any) => {
              const sum = (value ?? 0) + (record?.totalInputBytes ?? 0);
              const result = bytesFormat(sum);
              return result;
            },
          },
          {
            title: t('table.duration'),
            dataIndex: 'totalSessionTime',
            align: 'center' as const,
            render: hourFormat,
          },
          {
            title: t('table.localization'),
            dataIndex: 'sessions',
            align: 'center',
            render: (sessions: ISessionLocationData[]) => (
              <>
                {sessions &&
                sessions.length > 0 &&
                sessions.find(item => item.city !== null) ? (
                  <div
                    onClick={() => {
                      handleShowLocationInfoArrayClick(sessions);
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <Tooltip title={t('localization-title')}>
                      <VscRadioTower
                        style={{ marginTop: '2px', color: '#28BEA5' }}
                      />
                    </Tooltip>
                  </div>
                ) : (
                  'N/A'
                )}
              </>
            ),
          },
          {
            title: t('details'),
            dataIndex: 'sessions',
            align: 'center',
            render: (_: any, record: any) => (
              <>
                <AccountsModal
                  customerId={customerId}
                  objectId={record.objectId}
                />
              </>
            ),
          },
        ],
      );
    }

    return allColumns;
  }, [
    dataToShow,
    tableOnly,
    t,
    language,
    handleShowLocationInfoArrayClick,
    customerId,
  ]);

  const handleAddZeroInMonthNumber = useCallback(
    (value: string) => (value.length === 1 ? `0${value}` : value),
    [],
  );

  const handleDateChange = useCallback(
    (dateChange: Moment | null) => {
      const monthNumber = dateChange && dateChange.month() + 1;
      setSearchParams({
        year: dateChange && dateChange.year(),
        month: monthNumber && handleAddZeroInMonthNumber(`${monthNumber}`),
      });
    },
    [setSearchParams, handleAddZeroInMonthNumber],
  );

  const handleDateAccountsChange = useCallback(
    (dateChange: Moment | null) => {
      setSearchParams({
        initDate: (dateChange ?? moment()).format('YYYY-MM-DD'),
        endDate: (dateChange ?? moment()).format('YYYY-MM-DD'),
      });
    },
    [setSearchParams],
  );

  const tableFilters = useMemo(
    () => [
      ...[
        dataToShow === 'month' || dataToShow === 'cycle'
          ? {
              visible: true,
              label: t('table.acctDate'),
              element: (
                <AntdDatePicker
                  format="MMM/YYYY"
                  style={{ width: '100%' }}
                  disabledDate={d => d && d > moment().endOf('day')}
                  value={
                    year && month
                      ? moment(`${year}${month}`, 'YYYYMM')
                      : moment()
                  }
                  // @ts-ignore
                  onChange={handleDateChange}
                  picker="month"
                  allowClear={false}
                />
              ),
            }
          : {
              visible: true,
              label: t('table.date'),
              info: t('help-with-daily-sessions'),
              element: (
                <AntdDatePicker
                  allowClear={false}
                  format="DD/MM/YYYY"
                  disabledDate={d => d && d >= moment().startOf('day')}
                  showToday={false}
                  size="middle"
                  value={
                    date
                      ? moment(getDateWithISOFormat(date))
                      : moment().subtract(1, 'days')
                  }
                  style={{ width: '100%' }}
                  onChange={onChangeInitialDate}
                />
              ),
            },
      ],
      ...(!tableOnly
        ? [
            {
              visible: true,
              label: t('table.msisdn'),
              element: (
                <Search
                  onSearch={value => setSearchParams({ msisdn: value })}
                  onChange={({ target: { value } }) => setMsisdnValue(value)}
                  value={msisdnValue}
                  allowClear
                />
              ),
            },
            {
              visible: true,
              label: t('table.imsi'),
              element: (
                <Search
                  onSearch={value => setSearchParams({ imsi: value })}
                  onChange={({ target: { value } }) => setImsiValue(value)}
                  value={imsiValue}
                  allowClear
                />
              ),
            },
          ]
        : []),
    ],
    [
      handleDateChange,
      imsiValue,
      month,
      msisdnValue,
      setSearchParams,
      t,
      year,
      dataToShow,
      date,
      onChangeInitialDate,
      tableOnly,
    ],
  );

  const statusMenuOperations = useMemo(
    () => [
      {
        label: t('show-day'),
        value: tableOnly ? 'accounts' : 'day',
      },
      {
        label: t('show-month'),
        value: 'month',
      },
      {
        label: t('show-cycle'),
        value: 'cycle',
      },
    ],
    [t, tableOnly],
  );

  const customRowKey = useMemo(() => {
    if (dataToShow === 'day') {
      return 'objectId';
    }
    if (dataToShow === 'cycle') {
      return 'objectId';
    }
    return 'uniqueId';
  }, [dataToShow]);

  return (
    <div>
      <SessionLocationArray
        isOpen={isLocationOpen}
        onClose={() => setIsLocationOpen(false)}
        dataArray={locationArrayInfo}
      />

      {!tableOnly && (
        <StatusMenu
          itemActive={dataToShow}
          data={statusMenuOperations}
          handleSelectedCard={item => {
            setDataToShow(item.value as TableType);
            clearSearchParams();
          }}
          style={{ marginBottom: 16 }}
        />
      )}

      <Table
        columns={columns}
        data={data?.content}
        customRowKey={customRowKey}
        customRowKeyList={
          dataToShow === 'month'
            ? ['imsi', 'totalSessionTime', 'totalInputBytes']
            : undefined
        }
        loading={!data?.content}
        title={t('title')}
        tableKey={`sessions-${dataToShow}`}
        extraActions={
          <>
            {dataToShow === 'accounts' && (
              <AntdDatePicker
                onChange={handleDateAccountsChange}
                disabledDate={current => {
                  return current && current > moment().endOf('day');
                }}
                format="DD/MM/YYYY"
                size="middle"
                value={
                  initDate
                    ? moment(getDateWithISOFormat(initDate))
                    : moment(new Date())
                }
                style={{ width: 120 }}
              />
            )}
            {dataToShow === 'day' && <RequestDailySessionReport />}
            {dataToShow !== 'day' && dataToShow !== 'accounts' && (
              <RequestReport
                items={[
                  {
                    role: rolesConstants.REQUEST_REPORT,
                    filters: {
                      year: year ?? moment().year(),
                      month: month ?? moment().month() + 1,
                      realm: keycloak?.realm,
                    },
                    path: `/service-proxy/accts/${
                      dataToShow === 'month' ? 'by-month' : 'by-cycle'
                    }/report`,

                    type:
                      dataToShow === 'month'
                        ? 'RADACCT_SESSIONS_MONTH'
                        : 'RADACCT_SESSIONS_CYCLE',
                    method: 'post',
                  },
                ]}
              />
            )}
          </>
        }
        filters={
          tableOnly && dataToShow === 'accounts'
            ? undefined
            : {
                list: tableFilters,
                openByDefault: true,
                hasValuesInFilters: hasValuesInExtraParams,
                onClearFilters: () => clearSearchParams(),
              }
        }
        pagination={{
          page,
          linesPerPage,
          total: data?.totalElements || 0,
        }}
        onTableChange={setSearchParams}
      />
    </div>
  );
};

export default Sessions;
